import type {TriggerIntegrationConfigComponentProps} from '@cohort/merchants/apps';
import UserPropertyInput from '@cohort/merchants/apps/cohort-form/triggerIntegrations/common/UserPropertyInput';
import StepContentInput from '@cohort/merchants/apps/common/StepContentInput';
import StepDescriptionInput from '@cohort/merchants/apps/common/StepDescriptionInput';
import StepStatementInput from '@cohort/merchants/apps/common/StepStatementInput';
import StepTitleInput from '@cohort/merchants/apps/common/StepTitleInput';
import TriggerReferenceIdInput from '@cohort/merchants/apps/common/TriggerReferenceIdInput';
import type {Defaults} from '@cohort/merchants/apps/common/triggers/utils';
import {defaultMedia, parseDefaults} from '@cohort/merchants/apps/common/triggers/utils';
import LocalizedInput from '@cohort/merchants/components/form/input/LocalizedInput';
import LanguageSelectorInput from '@cohort/merchants/components/form/LanguageSelectorInput';
import Separator from '@cohort/merchants/components/Separator';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import type {CohortFormCheckboxTriggerStruct} from '@cohort/shared/apps/cohort-form/triggers/checkbox';
import type {Language} from '@cohort/shared/schema/common';
import {Fragment, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';

const getDefaults = (definedLanguages: Language[]): Defaults => {
  const defaults = {
    title: {},
    description: {},
  };
  return parseDefaults(defaults, definedLanguages);
};

const CohortFormCheckboxTriggerConfigComponent: React.FC<
  TriggerIntegrationConfigComponentProps<CohortFormCheckboxTriggerStruct>
> = ({formReturn: {reset, control, register, watch}, step, trigger}) => {
  const init = useRef(false);
  const merchant = useCurrentMerchant();

  const {t} = useTranslation('app-cohort-form', {
    keyPrefix: 'triggerIntegrations.checkbox',
  });

  const [selectedLanguage, definedLanguages] = watch(['selectedLanguage', 'definedLanguages']);
  const isDefaultLanguageSelected = selectedLanguage === merchant.defaultLanguage;

  useEffect(() => {
    if (init.current) {
      return;
    }
    const defaults = getDefaults(definedLanguages);
    reset(formValues => ({
      ...formValues,
      defaultTitle: defaults.title,
      defaultDescription: defaults.description,
      descriptionMode: 'custom',
      title: step?.title ?? {},
      description: step?.description ?? {},
      icon: step?.icon !== undefined ? step.icon : '🙂‍↕️',
      media: step?.media ?? defaultMedia,
      trigger: {
        id: trigger?.id ?? null,
        connectionId: null,
        triggerIntegrationId: 'cohort-form.checkbox',
        triggerIntegrationConfig: {
          referenceId: trigger?.triggerIntegrationConfig.referenceId ?? null,
          userPropertyId: trigger?.triggerIntegrationConfig.userPropertyId ?? null,
          checkboxText: trigger?.triggerIntegrationConfig.checkboxText ?? {},
          withUserProperty: !!trigger?.triggerIntegrationConfig.userPropertyId,
        },
      },
    }));
    init.current = true;
  }, [definedLanguages, reset, step, trigger]);

  if (!init.current) {
    return null;
  }

  return (
    <Fragment>
      <LanguageSelectorInput />
      <StepTitleInput />
      <StepDescriptionInput />
      <LocalizedInput
        type="text"
        label={t('labelCheckboxText')}
        name="trigger.triggerIntegrationConfig.checkboxText"
        register={register}
        control={control}
        selectedLanguage={selectedLanguage}
        optional={!isDefaultLanguageSelected}
      />
      <Separator />
      <StepContentInput />
      <Separator />
      <StepStatementInput />
      <Separator />
      <UserPropertyInput promptType="checkbox" />
      <Separator />
      <TriggerReferenceIdInput />
    </Fragment>
  );
};

export default CohortFormCheckboxTriggerConfigComponent;
